/**
 * Utility functions for managing authentication tokens in localStorage.
 * The storage key is distinct from the admin app so the two never collide
 * in the same browser.
 */

const TOKEN_KEY = "backoffice_access_token";
const TOKEN_EXPIRY_KEY = "backoffice_token_expiry";

export class TokenStorage {
  /**
   * Store the access token and its expiration time
   */
  static setToken(token: string): void {
    try {
      localStorage.setItem(TOKEN_KEY, token);

      // Calculate expiration time (24 hours from now as per API config)
      const expiryTime = new Date();
      expiryTime.setHours(expiryTime.getHours() + 24);
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toISOString());
    } catch (error) {
      console.error("Failed to store token:", error);
    }
  }

  /**
   * Retrieve the access token if it exists and is not expired
   */
  static getToken(): string | null {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const expiryString = localStorage.getItem(TOKEN_EXPIRY_KEY);

      if (!token || !expiryString) {
        return null;
      }

      const expiryTime = new Date(expiryString);
      const now = new Date();

      // Check if token is expired
      if (now >= expiryTime) {
        this.clearToken();
        return null;
      }

      return token;
    } catch (error) {
      console.error("Failed to retrieve token:", error);
      return null;
    }
  }

  /**
   * Remove the access token and expiry from storage
   */
  static clearToken(): void {
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
    } catch (error) {
      console.error("Failed to clear token:", error);
    }
  }

  /**
   * Check if a valid token exists
   */
  static hasValidToken(): boolean {
    return this.getToken() !== null;
  }

  /**
   * Get the token expiration time
   */
  static getTokenExpiry(): Date | null {
    try {
      const expiryString = localStorage.getItem(TOKEN_EXPIRY_KEY);
      return expiryString ? new Date(expiryString) : null;
    } catch (error) {
      console.error("Failed to get token expiry:", error);
      return null;
    }
  }

  /**
   * Check if token will expire soon (within 5 minutes)
   */
  static isTokenExpiringSoon(): boolean {
    try {
      const expiry = this.getTokenExpiry();
      if (!expiry) return false;

      const now = new Date();
      const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60 * 1000);

      return expiry <= fiveMinutesFromNow;
    } catch (error) {
      console.error("Failed to check token expiry:", error);
      return false;
    }
  }
}
